// The grids' type-to-filter rule (sc-114202): `*` wildcards over plain text. Pure — no React, no router, no DOM.
//
//   W1  case-insensitive;
//   W2  a pattern without `*` is an implicit contains (`beat` ≡ `*beat*`);
//   W3  a pattern with `*` must match the whole value, and `*` matches any run of characters, including none
//       (`heart*` starts with, `*beat` ends with, `h*t` both);
//   W4  every other character is literal: regex metacharacters are escaped, so `a.b` never matches `axb`;
//   W5  leading and trailing whitespace in the pattern is ignored; inner whitespace is literal;
//   W6  a blank pattern matches every value, and `filterByWildcard` returns its input array unchanged;
//   W7  a null/undefined value is tested as "", so only a blank or all-`*` pattern matches it;
//   W8  a compiled matcher is stateless (no `g` flag), so it is reusable across values.

function escapeRegExp(text: string): string {
  return text.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/** Compiles `pattern` once (W1–W8) into a predicate over one value. */
export function wildcardMatcher(pattern: string): (value: string | null | undefined) => boolean {
  const trimmed = pattern.trim();
  if (trimmed === "") return () => true;
  // `[\s\S]*` rather than an `s` flag: the build targets ES2017, and dotAll is ES2018.
  const body = trimmed.split("*").map(escapeRegExp).join("[\\s\\S]*");
  const regex = new RegExp(trimmed.includes("*") ? `^${body}$` : body, "i");
  return (value) => regex.test(value ?? "");
}

/** The rows any of whose `fields` match `pattern`, in their original order; `rows` itself for a blank pattern. */
export function filterByWildcard<T>(
  rows: T[],
  pattern: string,
  fields: (row: T) => ReadonlyArray<string | null | undefined>
): T[] {
  if (pattern.trim() === "") return rows;
  const matches = wildcardMatcher(pattern);
  return rows.filter((row) => fields(row).some(matches));
}
